/**
 * Evidence formatting for the diligence panels. Unlike the register's figures,
 * evidence states an explicit "Unknown" where an observation is missing, so a
 * gap is never read as zero. Presentation only: no value is derived here.
 */

const UNKNOWN = 'Unknown'

/** Pence to pounds, whole pounds; `compact` gives £139k for chart labels. */
export function money(pence: number | null | undefined, compact = false): string {
  if (pence === null || pence === undefined || !Number.isFinite(pence)) return UNKNOWN
  return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 0, notation: compact ? 'compact' : 'standard' }).format(pence / 100)
}

/** Grouped number with up to `digits` decimals; Unknown when absent. */
export function num(value: number | null | undefined, digits = 1, suffix = ''): string {
  return value === null || value === undefined || !Number.isFinite(value) ? UNKNOWN : `${new Intl.NumberFormat('en-GB', { maximumFractionDigits: digits }).format(value)}${suffix}`
}

/** Grouped number with no rounding beyond the locale default; a dash when absent. */
export function plain(value: number | null | undefined): string {
  return value === null || value === undefined ? '—' : new Intl.NumberFormat('en-GB').format(value)
}

export function pct(value: number | null | undefined, digits = 1): string {
  return num(value, digits, '%')
}

export function miles(value: number | null | undefined): string {
  return num(value, 2, ' mi')
}

export function date(value: string | null | undefined): string {
  if (!value) return UNKNOWN
  const parsed = new Date(value)
  return Number.isNaN(parsed.valueOf()) ? value : new Intl.DateTimeFormat('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }).format(parsed)
}

/** snake_case to Title Case; Unknown when absent. */
export function label(value: string | null | undefined): string {
  if (!value) return UNKNOWN
  return value.replaceAll('_', ' ').replace(/\b\w/g, character => character.toUpperCase())
}

/** Presentation alias only; source identifiers and external URLs keep their original values. */
export function neutral(value: string): string {
  if (/^(?:rightmoveMarket|propertyMarket)\.askingRent$/i.test(value)) return 'Local advertised rents'
  if (/^rightmoveMarket$/i.test(value)) return 'Local market'
  return value.replace(/rightmove(?:\.co\.uk|_co_uk)?/gi, 'Local listings')
}

export const SQFT_PER_SQM = 1 / 0.09290304
